import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";

export interface CommentReply {
  id: string;
  message: string;
  sender_id: string;
  created_at: string;
  attachments: any[];
  sender?: {
    id: string;
    full_name: string | null;
  };
}

export const useCommentReplies = (parentId: string | undefined) => {
  const queryClient = useQueryClient();

  const { data: replies = [], isLoading } = useQuery({
    queryKey: ["comment-replies", parentId],
    queryFn: async () => {
      if (!parentId) return [];

      const { data, error } = await supabase
        .from("inter_departmental_messages")
        .select(`
          id,
          message,
          sender_id,
          created_at,
          attachments
        `)
        .eq("parent_id", parentId)
        .order("created_at", { ascending: true });

      if (error) throw error;

      // Fetch sender profiles
      const senderIds = [...new Set(data?.map(r => r.sender_id) || [])];
      const { data: profiles } = await supabase
        .from("profiles")
        .select("id, full_name")
        .in("id", senderIds);

      const profileMap = new Map(profiles?.map(p => [p.id, p]) || []);

      return data?.map(r => ({
        ...r,
        attachments: (r.attachments as any[]) || [],
        sender: profileMap.get(r.sender_id),
      })) as CommentReply[];
    },
    enabled: !!parentId,
  });

  const { mutateAsync: sendReply, isPending: isSending } = useMutation({
    mutationFn: async ({ 
      message, 
      attachments = [],
      entityType,
      entityId,
      departmentId,
      mentionedUserIds = [],
    }: { 
      message: string; 
      attachments?: any[];
      entityType: string;
      entityId: string;
      departmentId: string;
      mentionedUserIds?: string[];
    }) => {
      const { data: session } = await supabase.auth.getSession();
      if (!session?.session?.user || !parentId) throw new Error("Not authenticated or no parent");

      const { data, error } = await supabase
        .from("inter_departmental_messages")
        .insert({
          message,
          sender_id: session.session.user.id,
          parent_id: parentId,
          entity_type: entityType,
          entity_id: entityId,
          department_id: departmentId,
          attachments,
        })
        .select()
        .single();

      if (error) throw error;

      // Parse @mentions from message and create notifications
      const mentionPattern = /@([A-Za-z\s]+?)(?=\s@|[.,!?;:\s]*$|[^a-zA-Z\s])/g;
      const mentions = message.match(mentionPattern) || [];
      
      // Get all profiles to match mention names
      const { data: profiles } = await supabase
        .from("profiles")
        .select("id, full_name");

      const mentionedFromText = mentions
        .map((m) => m.substring(1).trim().toLowerCase())
        .map((name) => profiles?.find((p) => p.full_name?.toLowerCase() === name)?.id)
        .filter(Boolean) as string[];

      const allMentionedUserIds = [...new Set([...mentionedUserIds, ...mentionedFromText])];

      if (allMentionedUserIds.length > 0) {
        const notifications = allMentionedUserIds
          .filter((id) => id !== session.session.user.id)
          .map((userId) => ({
            user_id: userId,
            type: "mention",
            title: "You were mentioned in a reply",
            message: message.substring(0, 100),
            related_entity_type: entityType,
            related_entity_id: entityId,
          }));

        if (notifications.length > 0) {
          await supabase.from("dashboard_notifications").insert(notifications);
        }
      }

      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["comment-replies", parentId] });
      queryClient.invalidateQueries({ queryKey: ["enhanced-comments"] });
    },
  });

  return {
    replies,
    isLoading,
    sendReply,
    isSending,
  };
};
